import { useEffect, useMemo } from 'react';

import { TourKeys } from '@cast/constants';
import { demoWoopWorkloadsList } from '@cast/fixtures';
import { WorkloadRecommendationStatusTypeEnum } from '@cast/types';
import { useSynchronizedRefetch, createScalarStore } from '@cast/utils';

import { useTourStepId } from 'core/tour-v2/hooks/useTourStepId';
import { TourSteps } from 'features/demo/types';
import { useWorkloadsQuery } from 'hooks/queries/workload-optimization';
import { useDataOrFixture } from 'hooks/useDataOrFixture';

import { useIsWoopDemoMode } from './useIsWoopDemoMode';

import { useCluster } from '@cast/shared-cluster-context-data-access';

const syncGroup = `${TourKeys.OpinionatedPlatform}-workloads-transition`;

/**
 * Multiple instances of `useWoopWorkloads` are rendered in WOOP list page
 * Defining the fixture index in global state, to avoid multiple versions of data in query cache during the data transition in OP demo mode
 * TODO (Justas): This should be handled during the `https://castai.atlassian.net/browse/UI-5682`
 */
const useIndex = createScalarStore(0);

export const useWoopWorkloads = ({
  enabled = true,
}: { enabled?: boolean } = {}) => {
  const { cluster } = useCluster();
  const isWoopDemoMode = useIsWoopDemoMode();
  const tourStepId = useTourStepId(TourKeys.OpinionatedPlatform);
  const index = useIndex().value;

  useEffect(() => useIndex.setValue(0), [tourStepId]);

  const demoWorkloadsFixture = useMemo(
    () => () => demoWoopWorkloadsList().then((data) => data.workloads),
    []
  );

  const queryResult = useWorkloadsQuery({
    clusterId: cluster.id,
    enabled: !isWoopDemoMode && enabled,
    demoFetchIndex: index,
    syncGroup,
  });

  const allApplied = useMemo(() => {
    return (
      queryResult.workloads?.length &&
      !queryResult.workloads?.some(
        (workload) =>
          workload.recommendationStatus.type ===
            WorkloadRecommendationStatusTypeEnum.APPLIED &&
          workload.recommendationStatus.appliedPods <
            workload.recommendationStatus.totalPods
      )
    );
  }, [queryResult.workloads]);

  // Use synchronized refetch during transition
  useSynchronizedRefetch({
    syncGroup,
    intervalMs: 1000,
    enabled: tourStepId === TourSteps.GoToAutoscalerSettings && !allApplied,
    onRefetch: () => {
      useIndex.setValue(index + 1);
    },
  });

  const workloadsDataOrFixture = useDataOrFixture(
    queryResult.workloads,
    demoWorkloadsFixture,
    isWoopDemoMode
  );

  return { ...queryResult, workloads: workloadsDataOrFixture };
};
